import { colors, primaryGradient } from '../../../theme/colors';
import {
  Box,
  ListItem,
  styled,
  Card,
  Button,
  createTheme,
} from "@mui/material";
import { gap, keyframes } from "@mui/system";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
`;

export const HomeContainer = styled(Box)(() => ({
  background: "linear-gradient(135deg, #fef6f6 0%, #f8f9fb 100%)",
  minHeight: "100vh",
  padding: "20px",

  "& .PDF__upload--container": {
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
    maxWidth: "150px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px",
    marginTop: "10px",
    backgroundColor: "#fff",
    border: "1px solid #e0e7ff",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    ":hover": {
      cursor: "pointer",
      boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
      transform: "translateY(-2px)",
      borderColor: colors.primary,
    },
    ":active": {
      transform: "translateY(0px)",
      transition: "transform 0.1s ease",
    },
    "& .iframe__PDF": {
      height: "100%",
      width: "100%",
      border: "none",
      borderRadius: "8px",
    },
    "& .input__pdf": {
      display: "none",
    },
    "& .font__gray": {
      color: "#6b7280",
    },
  },

  "& .JD__filename": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "100%",
    fontWeight: 500,
    color: "#374151",
  },

  "& .PDF__card": {
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
    borderRadius: "12px",
    padding: "24px 20px 20px 20px",
    position: "relative",
    backgroundColor: "#fff",
    border: "1px solid #f1f5f9",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
      transform: "translateY(-2px)",
    },
  },

  "& .cancel__button": {
    position: "absolute",
    right: "8px",
    top: "8px",
    color: "#ef4444",
    "&:hover": {
      backgroundColor: colors.primaryLight,
    },
  },

  "& .btn": {
    display: "block",
    backgroundColor: colors.primary,
    color: "#fff",
    textTransform: "none",
    margin: "16px 0px",
    alignItems: "center",
    borderRadius: "12px",
    fontWeight: 600,
    padding: "12px 24px",
    boxShadow: "0 4px 12px rgba(127, 29, 29, 0.3)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      backgroundColor: colors.primaryHover,
      boxShadow: "0 6px 20px rgba(127, 29, 29, 0.4)",
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0px)",
    },
  },

  "& table": {
    width: "100%",
    minWidth: "1200px",
  },

  "& .MuiTableCell-head": {
    color: "#fff !important",
    fontSize: "13px",
    fontWeight: 600,
    backgroundColor: colors.primary,
    backgroundImage: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
    whiteSpace: "nowrap",
    letterSpacing: "0.3px",
    padding: "14px 16px",
  },

  "& .table_user-primary-detail": {
    display: "flex",
    gap: 12,
    alignItems: "center",
    "& > div > p": {
      fontSize: "0.875rem",
      "&:nth-of-type(2)": {
        color: "#6b7280",
      },
    },
  },

  "& .radio__buttons--group": {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
  },

  "& .summary__listitem--container": {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    padding: "16px",
    borderRadius: "8px",
    backgroundColor: "#fff",
    "&:not(:last-child)": {
      borderBottom: "1px solid #e5e7eb",
    },
  },

  "& .tab__container": {
    marginTop: "20px",
    animation: `${fadeIn} 0.6s ease-out`,
  },

  "& .MuiListItemIcon-root": {
    minWidth: "20px",
    "& svg": {
      width: "12px !important",
      height: "19px !important",
    },
  },

  "& .pointer--hover": {
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    ":hover": {
      cursor: "pointer",
      boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
      transform: "translateY(-2px)",
    },
    ":active": {
      transform: "translateY(0px)",
    },
  },
}));

export const HeaderContainer = styled(Box)({
  marginBottom: "32px",
  textAlign: "center",
  "& .primary-heading": {
    color: colors.primary,
    fontWeight: 700,
    marginBottom: "16px",
    fontSize: "2rem",

    "@media (max-width: 768px)": {
      fontSize: "2.2rem",
    },
  },
});

export const StyledFormSection = styled(Box)(({ theme }) => ({
  "& .years-hint-text": {
    marginLeft: theme.spacing(1),
    color: theme.palette.text.disabled,
    fontStyle: "italic",
  },

  "& .custom-year-input .MuiOutlinedInput-root": {
    color: "#000",
    backgroundColor: "#fff",
    opacity: 1,
  },

  "& .custom-year-select.Mui-disabled .MuiOutlinedInput-root": {
    backgroundColor: "#f5f5f5",
    opacity: 0.7,
  },

  "& .custom-year-option": {
    color: "#000",
    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: `${colors.primary} !important`,
      color: "#fff !important",
    },
    "&.Mui-selected": {
      backgroundColor: colors.primary,
      color: "#fff",

      "&:hover": {
        backgroundColor: `${colors.primary} !important`,
      },
    },
    "&.Mui-focusVisible": {
      backgroundColor: colors.primary,
      color: "#fff",
    },
  },

  "& .custom-year-select .MuiAutocomplete-popupIndicator": {
    color: colors.primary,
  },

  "& .custom-year-select.Mui-disabled .MuiAutocomplete-popupIndicator": {
    color: "#bdbdbd",
  },

  "& .custom-year-select .MuiAutocomplete-clearIndicator": {
    color: colors.primary,
  },

  "& .custom-year-select.Mui-disabled .MuiAutocomplete-clearIndicator": {
    color: "#bdbdbd",
  },
}));

export const CreateJDContainer = styled(Box)({
  animation: `${slideUp} 0.8s ease-out`,
  display: "flex",
  padding: "40px 50px",
  backgroundColor: "#fff",
  borderRadius: "24px",
  margin: "0 0 10px 0",
  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
  position: "relative",
  overflow: "auto",

  "& .create-jd-paper": {
    borderRadius: 3,
    width: "100%",
  },

  "& .job-req-heading": {
    mb: "0.5rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    color: colors.primary,
    fontWeight: 600,
    fontSize: "1.5rem",
  },

  "& .page-subtitle": {
    color: "#64748b",
    fontSize: "0.95rem",
    lineHeight: "1.6rem",
    marginBottom: "1.5rem",
  },
});

export const FormSection = styled(Box)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
    transition: "all 0.3s ease",
    "& fieldset": {
      borderColor: "#d6e0ea",
    },
    "&:hover fieldset": {
      borderColor: colors.primary,
    },
    "&.Mui-focused fieldset": {
      borderColor: colors.primary,
      borderWidth: "1.5px",
    },
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    },
    "&.Mui-focused": {
      boxShadow: "0 4px 20px rgba(127, 29, 29, 0.1)",
    },
  },

  "& .form-label": {
    mb: 2,
    color: colors.primary,
    fontWeight: 600,
    marginBottom: "8px",
    fontSize: "1rem",
  },
});

export const StyledCard = styled(Card)({
  borderRadius: "20px",
  boxShadow: "0 8px 40px rgba(0, 0, 0, 0.08)",
  border: "1px solid #f1f5f9",
  overflow: "hidden",
  animation: `${slideUp} 0.8s ease-out`,

  "& .MuiCardHeader-root": {
    backgroundColor: "#fafbfc",
    borderBottom: "1px solid #e5e7eb",
    // padding: "24px 32px",
  },

  "& .MuiCardHeader-root .MuiButton-outlined": {
    borderColor: colors.primary,
    color: colors.primary,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: colors.primaryHover,
      backgroundColor: "rgba(127, 29, 29, 0.04)",
      boxShadow: "0 2px 8px rgba(127, 29, 29, 0.1)",
    },
  },

  "& .MuiCardContent-root": {
    padding: "32px",
    "&:last-child": {
      paddingBottom: "32px",
    },
  },

  "& .MuiDivider-root": {
    margin: "24px 0",
    backgroundColor: "#e5e7eb",
  },

  "& .MuiList-root": {
    padding: "8px 0",
  },

  "& .MuiListItem-root": {
    padding: "4px 0",
    alignItems: "flex-start",
  },

  "& .MuiChip-root": {
    borderRadius: "8px",
    fontWeight: 500,
    "&.MuiChip-outlined": {
      borderWidth: "1.5px",
    },
  },
});

export const SearchButtonContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  marginTop: "32px",
  paddingTop: "24px",
  borderTop: "1px solid #e0e0e0",
});

export const StyledSearchButton = styled(Button)({
  backgroundColor: colors.primary,
  color: "#fff",
  borderRadius: "16px",
  fontSize: "1.2rem",
  fontWeight: 600,
  textTransform: "none",
  minWidth: "250px",
  boxShadow: "0 8px 25px rgba(127, 29, 29, 0.3)",
  transition: "all 0.3s ease",

  "&:hover": {
    backgroundColor: colors.primaryHover,
    transform: "translateY(-3px)",
    boxShadow: "0 12px 30px rgba(127, 29, 29, 0.4)",
  },

  "&.Mui-disabled": {
    backgroundColor: `${colors.buttonDisabled.background} !important`,
    color: `${colors.buttonDisabled.color} !important`,
    transform: "none !important",
    boxShadow: "none !important",
    opacity: 1,
    cursor: "not-allowed",
  },
});

export const AnimatedButton = styled(Button)({
  borderRadius: "12px",
  textTransform: "none",
  fontWeight: 600,
  padding: "12px 32px",
  fontSize: "1rem",
  boxShadow: "0 4px 20px rgba(127, 29, 29, 0.3)",
  background: primaryGradient,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    boxShadow: "0 8px 30px rgba(127, 29, 29, 0.4)",
    transform: "translateY(-2px)",
    background: `linear-gradient(135deg, ${colors.primaryHover} 0%, #1565c0 100%)`,
  },

  "&:active": {
    transform: "translateY(0px)",
  },

  "&.Mui-disabled": {
    background: `${colors.buttonDisabled.background} !important`,
    color: `${colors.buttonDisabled.color} !important`,
    boxShadow: "none",
    transform: "none",
    opacity: 1,
    cursor: "not-allowed",
  },

  "&.MuiButton-root": {
    animation: `${pulse} 2s infinite`,
    "&:hover": {
      animation: "none",
    },
  },
});

export const GradientBackground = styled(Box)({
  background: "linear-gradient(135deg, #fef6f6 0%, #f8f9fb 100%)",
  minHeight: "100vh",
  padding: "20px 0",
});

export const HeaderSection = styled(Box)({
  textAlign: "center",
  marginBottom: "40px",
  animation: `${fadeIn} 1s ease-out`,

  "& .MuiTypography-h4": {
    background: primaryGradient,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "16px",
  },
});

export const FormCard = styled(Card)({
  borderRadius: "20px",
  boxShadow: "0 8px 40px rgba(0, 0, 0, 0.08)",
  border: "1px solid #f1f5f9",
  overflow: "hidden",
  backgroundColor: "#fff",

  "& .form-header": {
    backgroundColor: "#fafbfc",
    borderBottom: "1px solid #e5e7eb",
    padding: "24px 32px 16px 32px",
  },
});

export const SkillChip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  padding: "6px 12px",
  backgroundColor: "#f0f9ff",
  color: "#0369a1",
  borderRadius: "20px",
  fontSize: "0.875rem",
  fontWeight: 500,
  margin: "4px",
  border: "1px solid #bae6fd",
  transition: "all 0.3s ease",

  "&:hover": {
    backgroundColor: "#e0f2fe",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(3, 105, 161, 0.2)",
  },
});

export const RequirementCard = styled(Card)({
  height: "100%",
  borderRadius: "16px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
    transform: "translateY(-2px)",
  },

  "& .MuiCardContent-root": {
    // padding: "24px",
    "&:last-child": {
      paddingBottom: "24px",
    },
  },
});

export const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "20px",

  "& .MuiTypography-root": {
    fontWeight: 600,
    color: colors.primary,
  },

  "& .MuiSvgIcon-root": {
    fontSize: "24px",
  },
});

export const ResponsibilityItem = styled(ListItem)({
  padding: "8px 0",
  alignItems: "flex-start",

  "& .MuiListItemIcon-root": {
    minWidth: "24px",
    marginTop: "8px",
  },

  "& .MuiListItemText-root": {
    margin: 0,
    "& .MuiTypography-root": {
      fontSize: "0.95rem",
      lineHeight: 1.6,
      color: "#4b5563",
    },
  },
});

export const WhyJoinUsCard = styled(Card)({
  background: primaryGradient,
  color: "#fff",
  borderRadius: "16px",
  boxShadow: "0 8px 30px rgba(127, 29, 29, 0.3)",

  "& .MuiCardContent-root": {
    padding: "32px",
    "&:last-child": {
      paddingBottom: "32px",
    },
  },

  "& .MuiTypography-root": {
    color: "#fff",
  },
});

export const LoadingOverlay = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,

  "& .loading-content": {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "40px",
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",

    "& .MuiTypography-root": {
      marginTop: "20px",
      color: colors.primary,
      fontWeight: 600,
    },
  },
});

export const FloatingActionButton = styled(Button)({
  position: "fixed",
  bottom: "30px",
  right: "30px",
  borderRadius: "50%",
  width: "60px",
  height: "60px",
  minWidth: "60px",
  boxShadow: "0 8px 25px rgba(127, 29, 29, 0.3)",
  background: primaryGradient,
  color: "#fff",
  zIndex: 1000,

  "&:hover": {
    boxShadow: "0 12px 35px rgba(127, 29, 29, 0.4)",
    transform: "translateY(-2px)",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "28px",
  },
});

export const StatsCard = styled(Card)({
  borderRadius: "16px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  // padding: "24px",
  textAlign: "center",
  transition: "all 0.3s ease",

  "&:hover": {
    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
    transform: "translateY(-2px)",
  },

  "& .stat-number": {
    fontSize: "2.5rem",
    fontWeight: 700,
    color: colors.primary,
    marginBottom: "8px",
  },

  "& .stat-label": {
    fontSize: "0.875rem",
    color: "#6b7280",
    fontWeight: 500,
  },
});

export const ProgressIndicator = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "16px",
  marginBottom: "32px",

  "& .step": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 16px",
    borderRadius: "20px",
    fontSize: "0.875rem",
    fontWeight: 500,

    "&.active": {
      backgroundColor: colors.primary,
      color: "#fff",
    },

    "&.completed": {
      backgroundColor: "#10b981",
      color: "#fff",
    },

    "&.pending": {
      backgroundColor: "#f3f4f6",
      color: "#6b7280",
    },
  },

  "& .step-connector": {
    width: "40px",
    height: "2px",
    backgroundColor: "#e5e7eb",

    "&.completed": {
      backgroundColor: "#10b981",
    },
  },
});

export const FeatureHighlight = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "16px",
  backgroundColor: "#f0f9ff",
  borderRadius: "12px",
  border: "1px solid #bae6fd",
  marginBottom: "16px",

  "& .MuiSvgIcon-root": {
    color: "#0369a1",
    fontSize: "24px",
  },

  "& .MuiTypography-root": {
    color: "#0c4a6e",
    fontWeight: 500,
  },
});

export const ActionButtonGroup = styled(Box)({
  display: "flex",
  gap: "16px",
  justifyContent: "center",
  marginTop: "32px",

  "& .MuiButton-root": {
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 600,
    padding: "12px 24px",
    minWidth: "140px",
  },

  "& .primary-button": {
    background: primaryGradient,
    color: "#fff",
    boxShadow: "0 4px 20px rgba(127, 29, 29, 0.3)",

    "&:hover": {
      boxShadow: "0 8px 30px rgba(127, 29, 29, 0.4)",
      transform: "translateY(-2px)",
    },
  },

  "& .secondary-button": {
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "1px solid #d1d5db",

    "&:hover": {
      backgroundColor: "#e5e7eb",
      transform: "translateY(-1px)",
    },
  },
});

export const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
    },
    secondary: {
      main: colors.secondary,
    },
    text: {
      primary: colors.primary,
      secondary: "#637381",
    },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      color: colors.primary,
    },
    h6: {
      color: colors.primary,
      fontWeight: 600,
    },
    body1: {
      color: colors.primary,
      lineHeight: 1.6,
    },
    subtitle1: {
      color: colors.primary,
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
  },
});

export const SoftSkillsWrapper = styled(Box)(({ theme }) => ({
  "& .soft-paper": {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    height: "100%",
  },

  "& .soft-title": {
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },

  "& .soft-list-item": {
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
    paddingLeft: 0,
    paddingRight: 0,
  },

  "& .soft-list-text .MuiTypography-root": {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

export const PQContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),

  "& .pq-heading": {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },

  "& .list-item-text": {
    "& .MuiTypography-root": {
      color: theme.palette.text.secondary,
    },
  },
}));

export const WhyJoinContainer = styled(Box)({
  marginBottom: "1rem",
  "& .why-join-head": {
    marginBottom: "1rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },

  "& .why-join-paper": {
    backgroundColor: "primary.main",
    color: "white",
    borderRadius: "1rem",
    backgroundColor: "#fff",
  },

  "& .why-join-content": {
    lineHeight: 1.7,
  },
});
